/* eslint-disable no-return-await */
import Bugsnag from '@bugsnag/js';
import dynamic from 'next/dynamic';
import getConfig from 'next/config';
import router, { useRouter } from 'next/router';
import styled, { ThemeProvider } from 'styled-components';
import { useEffect, useState } from 'react';
import Cookie from 'js-cookie';

import { logOutUser } from 'auth/services/logOutUser';
import { ICmsBanner } from 'banner/types/cmsBanner';
import { FeatureFlags } from 'featureFlags/constants';
import { customCache } from 'homepage/utils/HomeTabCustomCache';
import { GoatOverlay } from 'layout/body/components/GoatOverlay';
import { TOGGABLE_THEME_PATHS } from 'layout/utils/toggableThemePaths';
import { darkThemePaths } from 'layout/utils/darkThemePaths';
import { getCmsBanner } from 'layout/utils/getCmsBanner';
import { t } from 'localization/utils/t';
import { SitePreferencesModalProvider } from 'shared/providers/SitePreferencesModalProvider';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { useLocation } from 'shared/hooks/useLocation';
import store from 'store';
import {
  setActiveTab,
  setIsLoading,
  setOpenTabScrollPosition,
  setVisitedByBackButton,
} from 'store/actions/homepageActions';
import { logout } from 'store/slice/authSlice';
import { setCurrentSearchTerm } from 'store/slice/searchSlice';
import { setFeatureFlags } from 'store/slice/featureFlagsSlice';
import { setIsHeaderVisible } from 'store/slice/pageLayoutSlice';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';
import { useFetchUserSegmentsQuery } from 'store/api/consumerSegmentAccess';
import { setCmsBanner } from 'store/slice/cmsBannerSlice';
import GlobalStyles from 'styles/global';
import { navigationThemes } from 'styles/theme/navigation';
import { theme } from 'styles/theme';
import { mParticleSetGuestUserAttr } from 'tracking/utils/mParticleServiceMethods';

const TP = 'layout.PageLayout';

const Footer = dynamic(() => import('layout/footer/components/Footer'));
const Header = dynamic(() => import('layout/header/components/Header'));
const PageLoader = dynamic(() => import('layout/header/components/PageLoader'));

interface ILayoutProps {
  children: React.ReactNode;
  cmsBanners?: ICmsBanner[];
  isCrawler: boolean;
  isInApp: boolean;
  isMobileUserAgent: boolean;
  xGoatSite: string;
}

export const PageLayout = ({
  children,
  cmsBanners,
  isCrawler,
  isInApp,
  isMobileUserAgent,
  xGoatSite,
}: ILayoutProps) => {
  const storeDispatch = useStoreDispatch();

  const [cypressFFDone, setCypressFFDone] = useState(false);

  const { featureFlags } = useStoreSelector((state) => state.featureFlags);
  const { windowSize } = useWindowSize();
  const { selectedCountryCode: countryCode } = useLocation();

  const { currentSearchTerm } = useStoreSelector((state) => state.search);
  const { currentTheme, isFooterVisible } = useStoreSelector((state) => state.pageLayout);
  const { isAuthenticated, user } = useStoreSelector((state) => state.auth);
  const { pathname, asPath } = useRouter();
  const {
    publicRuntimeConfig: { CDN_ASSET_PREFIX },
  } = getConfig();

  const [themes, setThemes] = useState({
    ...theme,
    navigation: darkThemePaths.includes(pathname) ? navigationThemes.dark : navigationThemes.light,
  });

  const isConsumerSegmentAccessEnabled =
    featureFlags?.[FeatureFlags.TEMP_WEB_ENABLE_CONSUMER_SEGMENT_ACCESS_CALL];
  const { data: userSegments, isLoading: isUserSegmentsLoading } = useFetchUserSegmentsQuery(
    {
      countryCode,
    },
    { skip: !isConsumerSegmentAccessEnabled },
  );
  const segmentIds = userSegments?.userSegmentsList?.map((segment) => segment.id);

  const showGoatOverlay = !isCrawler && !isInApp;
  const showDiscoverTabByDefault = useFeatureFlag(
    FeatureFlags.WEB_ENABLE_DEFAULT_HOME_TAB_DISCOVER,
  );

  useEffect(() => {
    if (
      featureFlags?.[FeatureFlags.WEB_ENABLE_GOAT_BANNER] &&
      cmsBanners &&
      !isUserSegmentsLoading
    ) {
      if (segmentIds) {
        const sortedBanners = getCmsBanner(cmsBanners, segmentIds);

        if (sortedBanners.length > 0) {
          store.dispatch(setCmsBanner({ ...sortedBanners?.[0], xGoatSite }));
        }
      } else {
        store.dispatch(setCmsBanner({ ...cmsBanners?.[0], xGoatSite }));
      }
    }
  }, [
    cmsBanners,
    featureFlags?.[FeatureFlags.WEB_ENABLE_GOAT_BANNER],
    segmentIds,
    isUserSegmentsLoading,
  ]);

  useEffect(() => {
    // @ts-ignore cypress sets this window value
    if (window?.Cypress && window?.cypressFeatures && !cypressFFDone) {
      // @ts-ignore cypress command set this value
      storeDispatch(setFeatureFlags(window.cypressFeatures));
      setCypressFFDone(true);
    }
  }, [featureFlags]);

  useEffect(() => {
    if (pathname === '/') {
      storeDispatch(setIsHeaderVisible(true));
    }

    try {
      mParticleSetGuestUserAttr(user);
    } catch (error) {
      Bugsnag.notify(error);
    }
  }, [isAuthenticated, pathname, user]);

  useEffect(() => {
    /*
     * to make sure that brand and manifesto pages
     * can have special theme rules
     * that won't be overwritten by default themes
     */

    const cleanPath = asPath?.split('?')?.[0];
    const isDarkPath = darkThemePaths.includes(pathname) || darkThemePaths.includes(cleanPath);
    if (TOGGABLE_THEME_PATHS[pathname] && currentTheme) {
      setThemes({
        ...theme,
        navigation: currentTheme,
      });
    } else {
      setThemes({
        ...theme,
        navigation: isDarkPath ? navigationThemes.dark : navigationThemes.light,
      });
    }
  }, [currentTheme, pathname, asPath]);

  useEffect(() => {
    /**
     * if a user clicks back and it's routing to the homepage,
     * check for the previous state of the homepage tabs and update the homepage state
     */
    const handlePopState = (e) => {
      if (e?.state?.url === '/') {
        const lastTabState = customCache.read();

        storeDispatch(setVisitedByBackButton(true));
        storeDispatch(setIsLoading(true));

        if (lastTabState) {
          storeDispatch(setActiveTab(lastTabState?.tabName));
          storeDispatch(setOpenTabScrollPosition(lastTabState?.scrollPosition));
        } else if (showDiscoverTabByDefault) {
          storeDispatch(setActiveTab(t(`${TP}.discover`, 'Discover')));
        } else {
          storeDispatch(setActiveTab(null));
        }
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    /**
     * Once a user has searched we only want to preserve the search term as long as the user
     * remains in the flow that would connect the search to a purchase. This checks if the user is
     * on a PDP or search page, if not set the term to the value constructor requested of TERM_UNKNOWN
     */
    const acceptedSearchTermPreservationRoutes = new Set([
      '/sneakers/[productTemplateSlug]',
      '/apparel/[productTemplateSlug]',
      '/search',
    ]);
    const handleRouteChangeComplete = (url) => {
      const urlIncludesQuery = url.includes(`query=`);
      if (
        !acceptedSearchTermPreservationRoutes.has(pathname) ||
        (pathname === `/search` && !urlIncludesQuery)
      ) {
        storeDispatch(setCurrentSearchTerm('TERM_UNKNOWN'));
      }
    };

    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSearchTerm, pathname]);

  const logoutGuestUser = async () => {
    await logOutUser();
    Cookie.remove('jwt');
    storeDispatch(logout());
  };

  useEffect(() => {
    window.addEventListener('beforeunload', () => {
      // eslint-disable-next-line no-console
      console.log('REFRESH TRIGGERED');
    });
  }, []);
  // if a guest user is anywhere else but checkout, we want to log them out
  useEffect(() => {
    if (asPath.startsWith('/checkout') === false && user?.isGuest) {
      void logoutGuestUser();
    }
  }, [asPath, user]);

  return (
    <PageLayout.Wrapper id="layout-wrapper" $minHeight={windowSize?.height}>
      <ThemeProvider theme={themes}>
        <SitePreferencesModalProvider>
          <GlobalStyles theme={themes} cdnAssetPrefix={CDN_ASSET_PREFIX} />
          {showGoatOverlay && <GoatOverlay />}
          <PageLoader />
          <Header isInApp={isInApp} isMobileUserAgent={isMobileUserAgent} />
          <main id="main-page-layout">{children}</main>
          {isFooterVisible && <Footer isInApp={isInApp} />}
        </SitePreferencesModalProvider>
      </ThemeProvider>
    </PageLayout.Wrapper>
  );
};

PageLayout.Wrapper = styled.div<{ $minHeight?: number }>`
  display: flex;
  flex-direction: column;

  min-height: ${({ $minHeight }) => ($minHeight ? `${$minHeight}px` : `100vh`)};

  > main {
    flex: 1 1 auto;
  }
`;
